import { useQuery } from "@tanstack/react-query"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart.tsx"
import { Label } from "@/components/ui/label.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"

import GasChartTooltip from "./GasChartTooltip.tsx"

import type { GasChartDataPoint } from "../types/gasChartTypes.ts"

export default function GasChart({
  fetcher,
  location,
}: {
  fetcher: () => Promise<GasChartDataPoint[]>
  location: number
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["gasChart", { location }],
    queryFn: fetcher,
  })

  if (isPending)
    return (
      <div className="p-4 rounded-md shadow-sm border col-span-2 overflow-x-hidden">
        <h2 className="text-lg font-semibold">30 Day Gas Trend</h2>
        <Skeleton className="min-h-[425px] w-full mt-[10px]" />
      </div>
    )

  if (isError)
    return (
      <div className="p-4 rounded-md shadow-sm border col-span-2 overflow-x-hidden">
        <h2 className="text-lg font-semibold">30 Day Gas Trend</h2>
        <div className="text-status-bad min-h-[425px] w-full mt-[10px] font-semibold">
          Network Error
        </div>
      </div>
    )

  const chartConfig = {
    gas: {
      label: "Pipeline",
      color: "var(--chart-1)",
    },
    flared: {
      label: "Flared",
      color: "var(--chart-2)",
    },
  } satisfies ChartConfig

  return (
    <div className="p-4 rounded-md shadow-sm border col-span-2 overflow-x-hidden bg-card">
      <h2 className="text-lg font-semibold">30 Day Gas Trend</h2>
      <Label className="text-muted-foreground text-[12pt]">
        <i>(MMBtu)</i>
      </Label>
      <ChartContainer className="min-h-[300px] w-full max-h-[450px]" config={chartConfig}>
        <BarChart data={data} margin={{ top: 25, left: -20, right: 10 }}>
          <defs>
            <linearGradient id="fillGas" x1="0" y1="0" x2="0" y2="1">
              <stop offset="25%" stopColor="var(--color-gas)" stopOpacity={1} />
              <stop offset="95%" stopColor="var(--color-gas)" stopOpacity={0.4} />
            </linearGradient>
            <linearGradient id="fillFlared" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--color-flared)" stopOpacity={1} />
              <stop offset="95%" stopColor="var(--color-flared)" stopOpacity={0.2} />
            </linearGradient>
          </defs>
          <XAxis dataKey="date" minTickGap={10} tickLine={false} />
          <YAxis tickLine={false} />
          <ChartTooltip content={<GasChartTooltip />} />
          <ChartLegend content={<ChartLegendContent />} />
          <CartesianGrid vertical={false} />
          <Bar dataKey="gas" stackId="a" fill="var(--color-gas)" />
          <Bar dataKey="flared" stackId="a" fill="var(--color-flared)" />
        </BarChart>
      </ChartContainer>
    </div>
  )
}
